import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum SelkirkPurchaseOrderLineItemKeys {
  itemQty = 'itemQty', // quantity of the item for order
  sku = 'sku', // SKU / ID of the item
  itemName = 'itemName', // descriptive name of the item (renamed from 'name')
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const SelkirkPurchaseOrderaddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
  combineAddressLines: yup.string().nullable().optional(),
})
export type SelkirkPurchaseOrderFileAddressData = yup.InferType<
  typeof SelkirkPurchaseOrderaddressInfoSchema
>

export const SelkirkPurchaseOrderVendorInfoSchema = yup.object().shape({
  name: yup.string().nullable().optional(),
  reference: yup.string().nullable().optional(),
  terms: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  address: SelkirkPurchaseOrderaddressInfoSchema,
})

export type SelkirkPurchaseOrderFileVendorData = yup.InferType<
  typeof SelkirkPurchaseOrderVendorInfoSchema
>

const miscChargeSchema = yup.object().shape({
  label: yup.string().nullable().optional(), // e.g. "Tax", "Shipping", "Freight"
  value: yup.string().nullable().optional(), // dollar amount
  percentage: yup.string().nullable().optional(), // e.g. "5" → renders "Tax (5%)"
})

const lineItemSchema = yup.object().shape({
  itemQty: yup.string().nullable().optional(),
  sku: yup.string().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
})

const SelkirkPurchaseOrderitemHeadersSchema = yup.object().shape({
  itemQty: yup.string().nullable().optional(),
  sku: yup.string().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

export const SelkirkPurchaseOrderFileDataSchema = yup.object().shape({
  deliverTo: SelkirkPurchaseOrderaddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  poDate: yup.string().nullable().optional(),
  shipTo: SelkirkPurchaseOrderaddressInfoSchema,
  vendor: SelkirkPurchaseOrderVendorInfoSchema,
  itemHeaders: SelkirkPurchaseOrderitemHeadersSchema,
  items: yup.array().of(lineItemSchema).required(),
  items2: yup.array().of(lineItemSchema).optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
  miscCharges: yup.array().of(miscChargeSchema).optional(),
  currency: yup.string().nullable().optional(),
})

export type SelkirkPurchaseOrderFileData = yup.InferType<typeof SelkirkPurchaseOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<SelkirkPurchaseOrderFileData>

export const SelkirkPurchaseOrderFileMappings: RequiredFileData = {
  deliverTo: {
    attn: '7dbf7a42-3f7c-4457-a517-d50949b2f5d4',
    addressLine1: 'a10d4a40-769a-444f-828c-193706f9ea3e',
    addressLine2: '697ce43d-cefa-43df-a652-55207b5a088e',
    city: 'c9335981-78bf-4025-b08e-830c2a62eef6',
    state: '09bec2f9-77dd-4433-a62c-4823ec853d01',
    zipCode: '9508c83b-3e5b-4365-9560-b1af2eba24d4',
    country: '09b09cac-8ce4-46f6-ae2a-66ac5c5edef3',
    displayMultipleLines: '1064fb1d-e487-43af-aa32-29fe9b3c2047',
    combineAddressLines: '767067b4-5105-4cc0-aead-79c6c47ce09f',
  },
  orderNumber: 'c0be0c41-8f21-4009-84b9-b0bf90f60ec5',
  poDate: '393ea31a-153a-4f8f-9fba-cdd4b60b1f7b',
  shipTo: {
    attn: 'a2a7bb11-47ae-4a85-894f-78e8a55a68ce',
    addressLine1: 'd9b6c5c8-44fc-4adc-8517-e772cfb35c05',
    addressLine2: '229b83dd-d9c4-44ec-99a6-14b0480235ea',
    city: '522b3510-1b4c-49ef-b4d4-47b42384caf1',
    state: 'b1386483-6821-4aa3-878a-022a1b1e82c1',
    zipCode: '99ad5b58-b436-49f2-bd88-8264049e9b6f',
    country: '32762aed-c2e8-403d-a926-75a04b1ca831',
    displayMultipleLines: 'd020c77c-eeac-4cde-a292-522bd9d7f066',
    combineAddressLines: '3be5ba7f-b40b-4c30-929b-e58cd5a2a915',
  },
  vendor: {
    name: 'c4a2d368-cc46-4d43-8812-307eac8c4bc3',
    reference: 'e78cb697-aba8-41a2-bac0-d6817f8a3117',
    terms: 'b4c0ef86-ddcd-4764-936e-2a6e4edeab0a',
    emailAddress: 'f449c34a-af83-4194-9118-3c2caab64577',
    phoneNumber: 'a7e44084-8e65-419a-a241-90013c9bc36a',
    address: {
      attn: '34582f37-6ce2-4377-9a2c-de6f081c369c',
      addressLine1: '838c918f-2071-48f4-a7cf-0d28a354ebac',
      addressLine2: '6a448a40-fec0-43e5-9ea9-c8a1d1e3b3f6',
      city: '6afdcb78-03e6-4b75-978c-1ba2c23e252f',
      state: '39c9a9f4-cf8f-4a61-b51c-f07785654c6d',
      zipCode: 'b0ca412c-aab7-4fd6-b2f5-518ada4fe5b6',
      country: '799126b3-4a1e-411b-8882-bf88296d7869',
      displayMultipleLines: 'ad5e8400-3f7a-4642-97e7-31bad7aafd80',
      combineAddressLines: 'cd0e8304-7af1-462e-80ad-90d23f6275c9',
    },
  },
  itemHeaders: {
    itemQty: '0432f272-5308-4afb-9810-af9657333485',
    sku: 'c9bbc6f6-551d-45fc-aebc-a25869498ca3',
    itemName: '3c6e9fb8-f3cc-4802-85b5-858d53d86c85',
    unitPrice: '4cbacc25-9783-4f13-8edc-22d9e3c270e9',
    lineTotal: '95edb3a8-889a-4261-99fc-26adb938686d',
  },
  items: [
    {
      itemQty: '1fd7e4c1-9351-41eb-9e28-e5267682ba06',
      sku: 'cd975d65-b24a-4dac-8236-beee388f2056',
      itemName: '61e41fb6-da42-4d78-bf20-505d8dc40310',
      unitPrice: 'af6d5860-fc89-41b5-bc39-e4cc9f035491',
    },
  ],
  items2: [
    {
      itemQty: '90324de6-6601-4cf3-b74d-685243d83cad',
      sku: 'bb760d84-69e7-470e-aaf7-1772f4d1a1e2',
      itemName: '19715f3a-9073-4389-a93c-6cbc72141bf5',
      unitPrice: '7ba2e315-1dfb-4d90-98a7-b3382189f34e',
    },
  ],
  dontRoundUnitPrice: 'b5026e2b-2ea3-4f54-8ac4-45ee61e9f854',
  instructions: '58c78680-63b4-4e43-8398-8279de7e6e7c',
  useLocaleQuantityFormatting: '40b01017-2595-4b1f-8f5d-5e0f44f5bbec',
  miscCharges: [
    {
      label: '73eafad4-5ea7-4756-98fa-ba07cd7a5f36',
      value: '08734a2b-7be6-4881-a0d7-badff84740d3',
      percentage: 'ad5d6d2b-2c7d-4aa4-959c-d098e5eb4573',
    },
  ],
  currency: '11e28139-7177-42a1-9301-f3ccaf91d186',
}
